import { TranslocoService } from '@ngneat/transloco';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import {
  ClauseOptionType,
  DocumentProcessingClause,
  DocumentProcessingPagedReviewResponse,
  FlagType,
  ShortReviewClauseResponseItem,
} from 'src/app/modules/documents/services/document-processing-api.service';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { WorkspaceDocumentDto } from '../../models/workspaces.model';
import {
  ClauseAlignmentRowViewModel,
  ClauseAlignmentStatus,
  ClauseVarianceExportType,
  ClauseVariancePdfContext,
  DetailedClauseAlignmentStatus,
  DetailedFilterModel,
} from './clause-variance-analysis.helper.models';
import { ClauseVarianceDetailedExportRow } from './services/clause-variance-export-state.service';

export class ClauseVarianceAnalysisHelper {
  private static readonly MIN_PDF_GENERATION_DURATION_MS = 1;
  private static readonly PDF_LEFT_MARGIN = 40;
  private static readonly PDF_RIGHT_MARGIN = 40;
  private static readonly PDF_LOGO_TOP_Y = 14;
  private static readonly PDF_LOGO_WIDTH = 146;
  private static readonly PDF_LOGO_HEIGHT = 46;
  private static readonly PDF_LOGO_ASSET_PATH = 'assets/autolex_logo.png';
  private static readonly PDF_HEADER_BOTTOM_Y = 72;
  private static readonly PDF_PAGE_TOP_Y = this.PDF_HEADER_BOTTOM_Y;
  private static readonly PDF_TITLE_Y = this.PDF_HEADER_BOTTOM_Y;
  private static readonly PDF_AFTER_TITLE_Y = this.PDF_TITLE_Y + 22;
  private static readonly PDF_TABLE_BOTTOM_MARGIN = 40;
  private static readonly PDF_PAGE_NUMBER_BOTTOM_OFFSET = 16;
  private static readonly PDF_PAGE_NUMBER_FONT_SIZE = 12;
  private static readonly PDF_SECTION_TITLE_OFFSET = 10;
  private static readonly PDF_SECTION_END_GAP = 18;
  private static readonly PDF_FILTER_TEXT_TOP_GAP = 8;
  private static readonly PDF_FILTER_TEXT_OFFSET = 14;
  private static readonly PDF_OVERVIEW_INTRO_TOP_GAP = 8;
  private static readonly PDF_OVERVIEW_INTRO_BOTTOM_GAP = 28;
  private static readonly PDF_OVERVIEW_TITLE_TOP_GAP = 14;
  private static readonly PDF_OVERVIEW_METADATA_TOP_GAP = 8;
  private static readonly PDF_OVERVIEW_METADATA_LINE_GAP = 12;
  private static readonly PDF_OVERVIEW_METADATA_VALUE_GAP = 4;
  private static readonly PDF_OVERVIEW_REFERENCE_TITLE_GAP = 22;
  private static readonly PDF_DASHBOARD_TITLE_TOP_GAP = 22;
  private static readonly PDF_PAGE_BREAK_THRESHOLD = 120;
  private static readonly PDF_TITLE_FONT_SIZE = 18;
  private static readonly PDF_SECTION_TITLE_FONT_SIZE = 14;
  private static readonly PDF_OVERVIEW_TEXT_FONT_SIZE = 11;
  private static readonly PDF_OVERVIEW_TEXT_LINE_HEIGHT = 10;
  private static readonly PDF_OVERVIEW_METADATA_FONT_SIZE = 9;
  private static readonly PDF_REFERENCE_SECTION_TITLE_FONT_SIZE = 11;
  private static readonly PDF_FILTER_TEXT_FONT_SIZE = 10;
  private static readonly PDF_TABLE_FONT_SIZE = 9;
  private static readonly PDF_DETAILED_TABLE_FONT_SIZE = 8;
  private static readonly PDF_DETAILED_TABLE_COLUMN_WIDTHS = [0.12, 0.15, 0.22, 0.07, 0.22, 0.22];
  private static readonly TABLE_MAP_BATCH_SIZE = 250;
  private static readonly HEADER_BG_COLOR_RGB: [number, number, number] = [73, 186, 221];
  private static readonly STATUS_YES_BG_COLOR_RGB: [number, number, number] = [215, 240, 211];
  private static readonly STATUS_NO_BG_COLOR_RGB: [number, number, number] = [247, 211, 219];
  private static readonly STATUS_EMPTY_BG_COLOR_RGB: [number, number, number] = [239, 239, 239];
  private static readonly STATUS_TEXT_COLOR_RGB: [number, number, number] = [0, 0, 0];
  private static logoImageDataUrl: string | null = null;
  private static logoImageLoadingPromise: Promise<string | null> | null = null;

  // Builds and downloads the full Clause Variance Analysis PDF report.
  static async generateAndDownloadPdf(
    translocoService: TranslocoService,
    context: ClauseVariancePdfContext,
  ): Promise<void> {
    const startedAt = Date.now();
    const [{ jsPDF }, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    const autoTable = autoTableModule.default as any;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    await this.ensureLogoImageDataLoaded();
    this.drawPageHeader(doc);

    let cursorY = this.addReportTitle(doc, translocoService);
    cursorY = this.addOverviewSummarySection(doc, cursorY, translocoService, context);
    cursorY = await this.addDashboardSection(doc, autoTable, cursorY, translocoService, context);

    doc.addPage();
    this.drawPageHeader(doc);
    cursorY = this.PDF_PAGE_TOP_Y;

    cursorY = await this.addOverviewSection(doc, autoTable, cursorY, translocoService, context);

    doc.addPage();
    this.drawPageHeader(doc);
    cursorY = this.PDF_PAGE_TOP_Y;

    await this.addDetailedSection(doc, autoTable, cursorY, translocoService, context);

    this.drawPagination(doc);

    const fileName = this.getExportFileName(
      context.exportType,
      context.selectedReferenceDocument?.name,
    );

    await this.waitForMinimumGenerationDuration(startedAt);
    doc.save(fileName);
  }

  // Builds the exported PDF file name using report type, reference document and date.
  private static getExportFileName(
    exportType: ClauseVarianceExportType,
    referenceDocumentName?: string,
  ): string {
    const reportType = this.getExportTypeLabel(exportType);
    const saveReferenceDocumentName = this.sanitizeFileNamePart(
      referenceDocumentName || 'Reference Document',
    );
    const currentDate = this.getCurrentDateStamp();

    return `AutoLex_${reportType}_Variance_Report_${saveReferenceDocumentName}_${currentDate}.pdf`;
  }

  // Resolves the report type segment used in the exported file name.
  private static getExportTypeLabel(exportType: ClauseVarianceExportType): string {
    return exportType === ClauseVarianceExportType.Filtered ? 'Filtered' : 'Full';
  }

  // Returns a compact date stamp used in file names (ddmmyyyy).
  private static getCurrentDateStamp(): string {
    const currentDate = new Date();
    const day = `${currentDate.getDate()}`.padStart(2, '0');
    const month = `${currentDate.getMonth() + 1}`.padStart(2, '0');
    const year = `${currentDate.getFullYear()}`;

    return `${day}${month}${year}`;
  }

  // Removes invalid file name characters and normalizes whitespace.
  private static sanitizeFileNamePart(value: string): string {
    return value
      .replace(/[\\/:*?"<>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Draws the main report title and returns the next Y position.
  private static addReportTitle(doc: any, translocoService: TranslocoService): number {
    doc.setFontSize(this.PDF_TITLE_FONT_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.text(
      translocoService.translate('workspaces.clauseVarianceAnalysis'),
      this.PDF_LEFT_MARGIN,
      this.PDF_TITLE_Y,
    );

    return this.PDF_AFTER_TITLE_Y;
  }

  // Renders the dashboard page tables: reference doc, analyzed docs, and clause compliance.
  private static async addDashboardSection(
    doc: any,
    autoTable: any,
    startY: number,
    translocoService: TranslocoService,
    context: ClauseVariancePdfContext,
  ): Promise<number> {
    const summaryRows = [
      [
        translocoService.translate('workspaces.referenceDocument'),
        context.selectedReferenceDocument?.name ?? '-',
      ],
    ];

    const documentProgressRows = context.nonReferenceDocuments.map((document) => {
      return [
        document.name,
        this.getDocumentTypeLabel(document.type, context.docTypes),
        this.getLanguageLabel(document.languageId, context.contentLanguages),
        this.getPositionLabel(document.userPositionId, translocoService),
      ];
    });

    const clauseProgressRows = this.getClauseProgressRows(context).map((row) => [
      row.clauseName,
      `${row.progressPercent}%`,
    ]);

    autoTable(doc, {
      startY,
      head: [
        [
          translocoService.translate('workspaces.name'),
          translocoService.translate('labels.summary'),
        ],
      ],
      body: summaryRows,
      ...this.getBaseAutoTableOptions(doc, this.PDF_TABLE_FONT_SIZE),
    });

    await this.yieldToMainThread();

    startY = this.drawReferenceStyleTitle(
      doc,
      translocoService.translate('workspaces.pdfAnalyzedDocuments'),
      (doc as any).lastAutoTable.finalY + this.PDF_DASHBOARD_TITLE_TOP_GAP,
    );

    autoTable(doc, {
      startY,
      head: [
        [
          translocoService.translate('workspaces.document'),
          translocoService.translate('columns.docType'),
          translocoService.translate('columns.language'),
          translocoService.translate('columns.position'),
        ],
      ],
      body: documentProgressRows,
      ...this.getBaseAutoTableOptions(doc, this.PDF_TABLE_FONT_SIZE),
    });

    await this.yieldToMainThread();

    startY = this.drawReferenceStyleTitle(
      doc,
      translocoService.translate('workspaces.clauseLevelCompliance'),
      (doc as any).lastAutoTable.finalY + this.PDF_DASHBOARD_TITLE_TOP_GAP,
    );

    autoTable(doc, {
      startY,
      head: [[translocoService.translate('workspaces.clause'), '']],
      body: clauseProgressRows,
      ...this.getBaseAutoTableOptions(doc, this.PDF_TABLE_FONT_SIZE),
    });

    return (doc as any).lastAutoTable.finalY + this.PDF_SECTION_END_GAP;
  }

  // Renders the clause alignment matrix section with subtitle and styled status cells.
  private static async addOverviewSection(
    doc: any,
    autoTable: any,
    startY: number,
    translocoService: TranslocoService,
    context: ClauseVariancePdfContext,
  ): Promise<number> {
    const rows = this.getClauseAlignmentRows(
      context.analyzingClauseVarianceResponse?.playbook?.clauses ?? [],
      context.nonReferenceDocuments,
      context.pagedReviewDataByDocumentId,
    );

    const head = [
      [
        translocoService.translate('workspaces.clause'),
        ...context.nonReferenceDocuments.map((document) => document.name),
      ],
    ];

    const body = rows.map((row) => [
      row.clauseName,
      ...row.documentStatuses.map((status) => this.getStatusLabel(translocoService, status)),
    ]);
    const yesLabel = translocoService.translate('buttons.yes');
    const noLabel = translocoService.translate('buttons.no');

    startY = this.drawSectionTitle(
      doc,
      translocoService.translate('workspaces.clauseAlignmentMatrix'),
      startY,
    );

    const referenceDocumentName = context.selectedReferenceDocument?.name ?? '-';
    const subtextText = translocoService.translate('workspaces.pdfClauseAlignmentMatrixSubtext', {
      referenceDocumentName,
    });
    startY = this.drawSectionSubtext(doc, startY, subtextText);

    await this.yieldToMainThread();

    autoTable(doc, {
      startY,
      head,
      body,
      ...this.getBaseAutoTableOptions(doc, this.PDF_TABLE_FONT_SIZE),
      rowPageBreak: 'auto',
      didParseCell: (data: any) => {
        this.applyYesNoCellStyles(data, yesLabel, noLabel, (columnIndex) => columnIndex > 0);
      },
    });

    return (doc as any).lastAutoTable.finalY + this.PDF_SECTION_END_GAP;
  }

  // Renders the detailed variance section including optional filtered-export summary text.
  private static async addDetailedSection(
    doc: any,
    autoTable: any,
    startY: number,
    translocoService: TranslocoService,
    context: ClauseVariancePdfContext,
  ): Promise<void> {
    const fullRows = this.getDetailedClauseAlignmentRows(
      context.analyzingClauseVarianceResponse?.playbook?.clauses ?? [],
      context.nonReferenceDocuments,
      context.pagedReviewDataByDocumentId,
    );

    const rowsSource = this.resolveDetailedRowsSource(context, fullRows);

    const body = await this.mapRowsInBatches(rowsSource, (row) => [
      row.clauseName,
      row.targetDocumentName,
      row.targetClause,
      row.legalEquivalence === DetailedClauseAlignmentStatus.Yes
        ? translocoService.translate('buttons.yes')
        : translocoService.translate('buttons.no'),
      row.legalDifferences,
      row.legalConsequences,
    ]);
    const yesLabel = translocoService.translate('buttons.yes');
    const noLabel = translocoService.translate('buttons.no');

    startY = this.drawSectionTitle(
      doc,
      translocoService.translate('workspaces.pdfDetailedVarianceAnalysisTitle'),
      startY,
    );

    const referenceDocumentName = context.selectedReferenceDocument?.name ?? '-';
    const detailedSubtext = translocoService.translate(
      'workspaces.pdfDetailedVarianceAnalysisSubtext',
      {
        referenceDocumentName,
      },
    );
    startY = this.drawSectionSubtext(doc, startY, detailedSubtext);

    if (
      context.exportType === ClauseVarianceExportType.Filtered &&
      context.detailedExportState.initialized
    ) {
      const filterText = this.getFilterSummaryText(
        translocoService,
        context.nonReferenceDocuments,
        context.detailedExportState.filters,
      );

      if (filterText) {
        startY += this.PDF_FILTER_TEXT_TOP_GAP;
        doc.setFontSize(this.PDF_FILTER_TEXT_FONT_SIZE);
        doc.setFont('helvetica', 'normal');
        doc.text(filterText, this.PDF_LEFT_MARGIN, startY);
        startY += this.PDF_FILTER_TEXT_OFFSET;
      }
    }

    await this.yieldToMainThread();

    autoTable(doc, {
      startY,
      head: [
        [
          translocoService.translate('workspaces.clause'),
          translocoService.translate('workspaces.targetDocument'),
          translocoService.translate('workspaces.targetClause'),
          translocoService.translate('workspaces.legalEquivalence'),
          translocoService.translate('workspaces.legalDifferences'),
          translocoService.translate('workspaces.legalConsequences'),
        ],
      ],
      body,
      ...this.getBaseAutoTableOptions(doc, this.PDF_DETAILED_TABLE_FONT_SIZE),
      tableWidth: this.getMaxTextWidth(doc),
      columnStyles: this.getDetailedTableColumnStyles(doc),
      rowPageBreak: 'auto',
      didParseCell: (data: any) => {
        this.applyYesNoCellStyles(data, yesLabel, noLabel, (columnIndex) => columnIndex === 3);
      },
    });
  }

  // Returns fixed column widths for the detailed variance table as page-width percentages.
  private static getDetailedTableColumnStyles(doc: any): Record<number, { cellWidth: number }> {
    const availableWidth = this.getMaxTextWidth(doc);

    return this.PDF_DETAILED_TABLE_COLUMN_WIDTHS.reduce<Record<number, { cellWidth: number }>>(
      (columnStyles, widthRatio, index) => {
        columnStyles[index] = { cellWidth: availableWidth * widthRatio };
        return columnStyles;
      },
      {},
    );
  }

  // Returns shared AutoTable options used by all report tables.
  private static getBaseAutoTableOptions(doc: any, fontSize: number): Record<string, unknown> {
    return {
      theme: 'grid',
      styles: { fontSize },
      headStyles: { fillColor: this.HEADER_BG_COLOR_RGB },
      margin: {
        left: this.PDF_LEFT_MARGIN,
        right: this.PDF_RIGHT_MARGIN,
        top: this.PDF_PAGE_TOP_Y,
        bottom: this.PDF_TABLE_BOTTOM_MARGIN,
      },
      didDrawPage: () => {
        this.drawPageHeader(doc);
      },
    };
  }

  // Draws section subtitle text and returns the next available Y position.
  private static drawSectionSubtext(doc: any, startY: number, subtext: string): number {
    const subtextY = startY + this.PDF_OVERVIEW_TITLE_TOP_GAP;
    doc.setFontSize(this.PDF_OVERVIEW_TEXT_FONT_SIZE);
    doc.setFont('helvetica', 'normal');

    const subtextBottomY = this.drawWrappedStyledText(
      doc,
      subtext,
      this.PDF_LEFT_MARGIN,
      subtextY,
      this.getMaxTextWidth(doc),
      this.PDF_OVERVIEW_TEXT_LINE_HEIGHT,
    );

    return subtextBottomY + this.PDF_OVERVIEW_METADATA_TOP_GAP;
  }

  // Returns the localized cell text for yes/no/empty clause alignment states.
  private static getStatusLabel(
    translocoService: TranslocoService,
    status: ClauseAlignmentStatus | null,
  ): string {
    if (status === ClauseAlignmentStatus.Yes) {
      return translocoService.translate('buttons.yes');
    }

    if (status === ClauseAlignmentStatus.No) {
      return translocoService.translate('buttons.no');
    }

    return '-';
  }

  // Chooses filtered rows for filtered exports when precomputed filters are initialized.
  private static resolveDetailedRowsSource(
    context: ClauseVariancePdfContext,
    fullRows: ClauseVarianceDetailedExportRow[],
  ): ClauseVarianceDetailedExportRow[] {
    if (context.exportType !== ClauseVarianceExportType.Filtered) {
      return fullRows;
    }

    return context.detailedExportState.initialized
      ? context.detailedExportState.filteredRows
      : fullRows;
  }

  // Applies consistent yes/no/empty cell styling for status columns.
  private static applyYesNoCellStyles(
    data: any,
    yesLabel: string,
    noLabel: string,
    shouldStyleColumn: (columnIndex: number) => boolean,
  ): void {
    if (data.section !== 'body' || !shouldStyleColumn(data.column.index)) {
      return;
    }

    const cellValue = `${data.cell.raw ?? ''}`.trim();

    if (cellValue === yesLabel) {
      data.cell.styles.fillColor = this.STATUS_YES_BG_COLOR_RGB;
      data.cell.styles.textColor = this.STATUS_TEXT_COLOR_RGB;
      data.cell.styles.halign = 'center';
      data.cell.styles.valign = 'middle';
      data.cell.styles.fontStyle = 'bold';
      return;
    }

    if (cellValue === noLabel) {
      data.cell.styles.fillColor = this.STATUS_NO_BG_COLOR_RGB;
      data.cell.styles.textColor = this.STATUS_TEXT_COLOR_RGB;
      data.cell.styles.halign = 'center';
      data.cell.styles.valign = 'middle';
      data.cell.styles.fontStyle = 'bold';
      return;
    }

    if (cellValue === '-') {
      data.cell.styles.fillColor = this.STATUS_EMPTY_BG_COLOR_RGB;
      data.cell.styles.textColor = this.STATUS_TEXT_COLOR_RGB;
      data.cell.styles.halign = 'center';
      data.cell.styles.valign = 'middle';
      data.cell.styles.fontStyle = 'normal';
    }
  }

  // Draws a standard section title and handles page-break rollover.
  private static drawSectionTitle(doc: any, sectionName: string, startY: number): number {
    const pageHeight = doc.internal.pageSize.getHeight();

    if (startY > pageHeight - this.PDF_PAGE_BREAK_THRESHOLD) {
      doc.addPage();
      this.drawPageHeader(doc);
      startY = this.PDF_PAGE_TOP_Y;
    }

    doc.setFontSize(this.PDF_SECTION_TITLE_FONT_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.text(sectionName, this.PDF_LEFT_MARGIN, startY);

    return startY + this.PDF_SECTION_TITLE_OFFSET;
  }

  // Draws a compact section title style used in dashboard subsections.
  private static drawReferenceStyleTitle(doc: any, sectionName: string, startY: number): number {
    const pageHeight = doc.internal.pageSize.getHeight();

    if (startY > pageHeight - this.PDF_PAGE_BREAK_THRESHOLD) {
      doc.addPage();
      this.drawPageHeader(doc);
      startY = this.PDF_PAGE_TOP_Y;
    }

    doc.setFontSize(this.PDF_REFERENCE_SECTION_TITLE_FONT_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.text(sectionName, this.PDF_LEFT_MARGIN, startY);

    return startY + this.PDF_SECTION_TITLE_OFFSET;
  }

  // Draws the first-page overview intro text and metadata block.
  private static drawOverviewSummary(
    doc: any,
    startY: number,
    translocoService: TranslocoService,
    context: ClauseVariancePdfContext,
  ): number {
    const maxTextWidth = this.getMaxTextWidth(doc);
    const analysisTypeLabel = this.getOverviewAnalysisTypeLabel(
      translocoService,
      context.exportType,
    );
    const generatedOnLabel = this.getCurrentDateLabel();
    const referenceDocumentName = context.selectedReferenceDocument?.name ?? '-';
    const introText = translocoService.translate('workspaces.clauseVarianceOverviewIntro', {
      workspaceName: context.workspaceName,
      referenceDocumentName,
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(this.PDF_OVERVIEW_TEXT_FONT_SIZE);
    const introY = startY + this.PDF_OVERVIEW_INTRO_TOP_GAP;
    const introBottomY = this.drawWrappedStyledText(
      doc,
      introText,
      this.PDF_LEFT_MARGIN,
      introY,
      maxTextWidth,
      this.PDF_OVERVIEW_TEXT_LINE_HEIGHT,
    );

    const overviewTitleY = introBottomY + this.PDF_OVERVIEW_INTRO_BOTTOM_GAP;
    doc.setFontSize(this.PDF_SECTION_TITLE_FONT_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.text(
      translocoService.translate('workspaces.overview'),
      this.PDF_LEFT_MARGIN,
      overviewTitleY,
    );

    const metadataStartY =
      overviewTitleY + this.PDF_OVERVIEW_TITLE_TOP_GAP + this.PDF_OVERVIEW_METADATA_TOP_GAP;

    doc.setFontSize(this.PDF_OVERVIEW_METADATA_FONT_SIZE);

    this.drawMetadataLine(
      doc,
      `${translocoService.translate('workspaces.analysisTypeLabel')}: `,
      analysisTypeLabel,
      metadataStartY,
    );

    const generatedOnLabelText = `${translocoService.translate('workspaces.generatedOnLabel')}: `;
    const generatedOnY = metadataStartY + this.PDF_OVERVIEW_METADATA_LINE_GAP;
    this.drawMetadataLine(doc, generatedOnLabelText, generatedOnLabel, generatedOnY);

    const referenceTitleY =
      metadataStartY + this.PDF_OVERVIEW_METADATA_LINE_GAP + this.PDF_OVERVIEW_REFERENCE_TITLE_GAP;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(this.PDF_REFERENCE_SECTION_TITLE_FONT_SIZE);
    doc.text(
      translocoService.translate('workspaces.referenceDocument'),
      this.PDF_LEFT_MARGIN,
      referenceTitleY,
    );

    return referenceTitleY + this.PDF_SECTION_TITLE_OFFSET;
  }

  // Wraps overview-summary drawing for readability at the call site.
  private static addOverviewSummarySection(
    doc: any,
    startY: number,
    translocoService: TranslocoService,
    context: ClauseVariancePdfContext,
  ): number {
    return this.drawOverviewSummary(doc, startY, translocoService, context);
  }

  // Draws one metadata line with bold label and normal value text.
  private static drawMetadataLine(
    doc: any,
    labelText: string,
    valueText: string,
    y: number,
  ): void {
    doc.setFont('helvetica', 'bold');
    doc.text(labelText, this.PDF_LEFT_MARGIN, y);
    const labelWidth = doc.getTextWidth(labelText);

    doc.setFont('helvetica', 'normal');
    doc.text(
      valueText,
      this.PDF_LEFT_MARGIN + labelWidth + this.PDF_OVERVIEW_METADATA_VALUE_GAP,
      y,
    );
  }

  // Draws wrapped text with inline bold markers using **bold** segments.
  private static drawWrappedStyledText(
    doc: any,
    textWithMarkers: string,
    startX: number,
    startY: number,
    maxWidth: number,
    lineHeight: number,
  ): number {
    const markerRegex = /\*\*(.*?)\*\*/g;
    const segments: { text: string; bold: boolean }[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = markerRegex.exec(textWithMarkers)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          text: textWithMarkers.slice(lastIndex, match.index),
          bold: false,
        });
      }

      segments.push({ text: match[1], bold: true });
      lastIndex = markerRegex.lastIndex;
    }

    if (lastIndex < textWithMarkers.length) {
      segments.push({ text: textWithMarkers.slice(lastIndex), bold: false });
    }

    if (!segments.length) {
      segments.push({ text: textWithMarkers, bold: false });
    }

    const maxX = startX + maxWidth;
    let currentX = startX;
    let currentY = startY;

    for (const segment of segments) {
      doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
      const tokens = segment.text.split(/(\s+)/);

      for (const token of tokens) {
        if (!token) {
          continue;
        }

        const isWhitespace = /^\s+$/.test(token);

        if (isWhitespace && currentX === startX) {
          continue;
        }

        const tokenWidth = doc.getTextWidth(token);

        if (!isWhitespace && currentX + tokenWidth > maxX) {
          currentY += lineHeight;
          currentX = startX;
        }

        if (isWhitespace && currentX === startX) {
          continue;
        }

        doc.text(token, currentX, currentY);
        currentX += tokenWidth;
      }
    }

    return currentY;
  }

  // Returns the localized overview analysis type label.
  private static getOverviewAnalysisTypeLabel(
    translocoService: TranslocoService,
    exportType: ClauseVarianceExportType,
  ): string {
    if (exportType === ClauseVarianceExportType.Filtered) {
      return translocoService.translate('workspaces.analysisTypeFilteredSelection');
    }

    return translocoService.translate('workspaces.analysisTypeFull');
  }

  // Returns the current date in the overview display format (dd/mm/yyyy).
  private static getCurrentDateLabel(): string {
    const currentDate = new Date();
    const day = `${currentDate.getDate()}`.padStart(2, '0');
    const month = `${currentDate.getMonth() + 1}`.padStart(2, '0');
    const year = `${currentDate.getFullYear()}`;

    return `${day}/${month}/${year}`;
  }

  // Ensures logo image data is loaded and cached before PDF header rendering.
  private static async ensureLogoImageDataLoaded(): Promise<void> {
    if (this.logoImageDataUrl) {
      return;
    }

    if (!this.logoImageLoadingPromise) {
      this.logoImageLoadingPromise = this.loadLogoImageDataUrl();
    }

    this.logoImageDataUrl = await this.logoImageLoadingPromise;
  }

  // Returns the maximum writable width for text content in the page body.
  private static getMaxTextWidth(doc: any): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    return pageWidth - this.PDF_LEFT_MARGIN - this.PDF_RIGHT_MARGIN;
  }

  // Loads the logo asset and converts it to a data URL for jsPDF image rendering.
  private static async loadLogoImageDataUrl(): Promise<string | null> {
    try {
      const response = await fetch(this.PDF_LOGO_ASSET_PATH);

      if (!response.ok) {
        return null;
      }

      return this.convertBlobToDataUrl(await response.blob());
    } catch {
      return null;
    }
  }

  // Converts a logo blob to a base64 data URL.
  private static convertBlobToDataUrl(blob: Blob): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' ? reader.result : null);
      };
      reader.onerror = () => reject(new Error('Failed to convert logo to data URL'));
      reader.readAsDataURL(blob);
    });
  }

  // Draws the logo in the page header area.
  private static drawPageHeader(doc: any): void {
    if (!this.logoImageDataUrl) {
      return;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoX = pageWidth - this.PDF_RIGHT_MARGIN - this.PDF_LOGO_WIDTH;

    doc.addImage(
      this.logoImageDataUrl,
      'PNG',
      logoX,
      this.PDF_LOGO_TOP_Y,
      this.PDF_LOGO_WIDTH,
      this.PDF_LOGO_HEIGHT,
    );
  }

  // Draws page numbers in n/N format on all pages.
  private static drawPagination(doc: any): void {
    const totalPages = doc.getNumberOfPages();

    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
      doc.setPage(pageNumber);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(this.PDF_PAGE_NUMBER_FONT_SIZE);
      doc.text(
        `${pageNumber}/${totalPages}`,
        pageWidth - this.PDF_RIGHT_MARGIN,
        pageHeight - this.PDF_PAGE_NUMBER_BOTTOM_OFFSET,
        { align: 'right' },
      );
    }
  }

  // Resolves the localized review status label based on current review flags.
  private static getReviewStatusLabel(
    translocoService: TranslocoService,
    context: ClauseVariancePdfContext,
  ): string {
    if (context.loadingReviewDataComplete && !context.reviewDoneWithErrors) {
      return translocoService.translate('workspaces.reviewStatusCompleted');
    }

    if (context.loadingReviewDataComplete && context.reviewDoneWithErrors) {
      return translocoService.translate('workspaces.reviewStatusCompletedWithErrors');
    }

    return translocoService.translate('workspaces.reviewStatusInProgress');
  }

  // Calculates a clamped completion percentage.
  private static getProgressPercent(reviewedPages: number, totalPages: number): number {
    if (!totalPages) {
      return 0;
    }

    return Math.min(Math.round((reviewedPages / totalPages) * 100), 100);
  }

  // Resolves a document type name by ID.
  private static getDocumentTypeLabel(
    docTypeId: number | undefined,
    docTypes: GetDocumentTypeDto[],
  ): string {
    return docTypes.find((docType) => docType.id === docTypeId)?.name ?? '-';
  }

  // Resolves a language display name by ID.
  private static getLanguageLabel(
    languageId: number | undefined,
    languages: LanguageDto[],
  ): string {
    return languages.find((language) => language.id === languageId)?.displayName ?? '-';
  }

  // Resolves a translated position label by ID with safe fallback.
  private static getPositionLabel(
    positionId: number | undefined,
    translocoService: TranslocoService,
  ): string {
    if (!positionId) {
      return '-';
    }

    const translatedPosition = translocoService.translate(`positions.${positionId}`);

    return translatedPosition === `positions.${positionId}` ? '-' : translatedPosition;
  }

  // Builds per-clause compliance percentage rows for the dashboard table.
  private static getClauseProgressRows(
    context: ClauseVariancePdfContext,
  ): { clauseName: string; progressPercent: number }[] {
    const clauses = context.analyzingClauseVarianceResponse?.playbook?.clauses ?? [];

    return clauses.map((clause) => {
      const matchingDocuments = context.nonReferenceDocuments.filter((document) => {
        const reviewClauses = context.pagedReviewDataByDocumentId[document.id]?.clauses ?? [];

        return reviewClauses.some(
          (reviewClause) =>
            reviewClause.id === clause.id && reviewClause.flagType === FlagType.MostDesired,
        );
      }).length;

      return {
        clauseName: clause.name,
        progressPercent: this.getProgressPercent(
          matchingDocuments,
          context.nonReferenceDocuments.length,
        ),
      };
    });
  }

  // Builds matrix rows containing clause status across all non-reference documents.
  private static getClauseAlignmentRows(
    playbookClauses: ShortReviewClauseResponseItem[],
    reviewDocuments: WorkspaceDocumentDto[],
    pagedReviewDataByDocumentId: Record<string, DocumentProcessingPagedReviewResponse>,
  ): ClauseAlignmentRowViewModel[] {
    return playbookClauses
      .map((clause) => ({
        clauseId: clause.id,
        clauseName: clause.name,
        documentStatuses: reviewDocuments.map((document) =>
          this.getClauseAlignmentStatus(clause.id, pagedReviewDataByDocumentId[document.id]),
        ),
      }))
      .filter((row) => row.documentStatuses.some((status) => status !== null));
  }

  // Computes one clause alignment status for a document review payload.
  private static getClauseAlignmentStatus(
    clauseId: number,
    pagedReviewData?: DocumentProcessingPagedReviewResponse,
  ): ClauseAlignmentStatus | null {
    const matchingClauses = pagedReviewData?.clauses.filter((clause) => clause.id === clauseId);

    if (!matchingClauses?.length) {
      return null;
    }

    return matchingClauses.some((clause) => clause.flagType === FlagType.MostDesired)
      ? ClauseAlignmentStatus.Yes
      : ClauseAlignmentStatus.No;
  }

  // Builds flattened detailed rows for detailed variance export table.
  private static getDetailedClauseAlignmentRows(
    playbookClauses: ShortReviewClauseResponseItem[],
    reviewDocuments: WorkspaceDocumentDto[],
    pagedReviewDataByDocumentId: Record<string, DocumentProcessingPagedReviewResponse>,
  ): ClauseVarianceDetailedExportRow[] {
    return reviewDocuments.flatMap((document) => {
      const pagedReviewData = pagedReviewDataByDocumentId[document.id];

      if (!pagedReviewData?.clauses?.length) {
        return [];
      }

      return playbookClauses.flatMap((clause) => {
        const reviewedClause = pagedReviewData.clauses.find(
          (reviewClause) => reviewClause.id === clause.id,
        );

        if (!reviewedClause) {
          return [];
        }

        return [
          {
            id: `${document.id}-${clause.id}`,
            clauseId: clause.id,
            clauseName: clause.name,
            targetDocumentId: document.id,
            targetDocumentName: document.name,
            targetClause: this.getMostDesiredTargetClause(reviewedClause),
            page: this.getProofPageNumbersLabel(reviewedClause.proofPageNumbers),
            legalEquivalence:
              reviewedClause.flagType === FlagType.MostDesired
                ? DetailedClauseAlignmentStatus.Yes
                : DetailedClauseAlignmentStatus.No,
            legalDifferences: this.getTextOrFallback(reviewedClause.legalDifferences),
            legalConsequences: this.getTextOrFallback(reviewedClause.legalConsequences),
          },
        ];
      });
    });
  }

  // Returns the Most Desired target clause text or a placeholder.
  private static getMostDesiredTargetClause(reviewedClause: DocumentProcessingClause): string {
    return (
      reviewedClause.playbookOptions.find((option) => option.type === ClauseOptionType.MostDesired)
        ?.text ?? '-'
    );
  }

  // Formats proof page numbers as comma-separated text.
  private static getProofPageNumbersLabel(proofPageNumbers: number[] | undefined): string {
    return proofPageNumbers?.length ? proofPageNumbers.join(', ') : '-';
  }

  // Returns trimmed text value or a placeholder for empty values.
  private static getTextOrFallback(value?: string): string {
    const trimmedValue = value?.trim();

    return trimmedValue ? trimmedValue : '-';
  }

  // Builds a localized one-line summary of active detailed export filters.
  private static getFilterSummaryText(
    translocoService: TranslocoService,
    nonReferenceDocuments: WorkspaceDocumentDto[],
    filters: DetailedFilterModel,
  ): string {
    const filterParts: string[] = [];

    if (filters.clauseName) {
      filterParts.push(
        `${translocoService.translate('workspaces.clause')} = ${filters.clauseName}`,
      );
    }

    if (filters.targetDocumentId) {
      const selectedDocument = nonReferenceDocuments.find(
        (document) => `${document.id}` === `${filters.targetDocumentId}`,
      );
      const targetDocumentValue = selectedDocument?.name || filters.targetDocumentId;

      filterParts.push(
        `${translocoService.translate('workspaces.targetDocument')} = ${targetDocumentValue}`,
      );
    }

    if (filters.legalEquivalence) {
      const legalEquivalenceValue =
        filters.legalEquivalence === DetailedClauseAlignmentStatus.Yes
          ? translocoService.translate('buttons.yes')
          : translocoService.translate('buttons.no');

      filterParts.push(
        `${translocoService.translate('workspaces.legalEquivalence')} = ${legalEquivalenceValue}`,
      );
    }

    if (!filterParts.length) {
      return '';
    }

    return `${translocoService.translate('workspaces.filterSummary')}: ${filterParts.join(', ')}`;
  }

  // Maps large row collections in small batches to keep UI responsive.
  private static async mapRowsInBatches<T, R>(
    items: T[],
    mapper: (item: T) => R,
    batchSize = this.TABLE_MAP_BATCH_SIZE,
  ): Promise<R[]> {
    const result: R[] = [];

    for (let index = 0; index < items.length; index += batchSize) {
      const currentBatch = items.slice(index, index + batchSize);
      result.push(...currentBatch.map(mapper));
      await this.yieldToMainThread();
    }

    return result;
  }

  // Yields execution to the event loop to avoid blocking the UI thread.
  private static yieldToMainThread(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  }

  // Ensures a minimum generation duration to avoid abrupt UX transitions.
  private static async waitForMinimumGenerationDuration(startedAt: number): Promise<void> {
    const elapsedMs = Date.now() - startedAt;
    const remainingMs = this.MIN_PDF_GENERATION_DURATION_MS - elapsedMs;

    if (remainingMs <= 0) {
      return;
    }

    return new Promise((resolve) => {
      setTimeout(resolve, remainingMs);
    });
  }
}
